import { useEffect, useRef, useState } from "react";
import { motion, useAnimationFrame, useMotionValue } from "framer-motion";

/**
 * "The inner mind unfolding": the light layer drawn over the head in the Better Questions
 * visual (homepage only, decorative). All coordinates are in the artwork's own 646 × 738
 * pixels, so everything stays locked to the head at every size.
 *
 * Awakening (once, when the visual first comes into view):
 *   a warm core of light ignites at the centre of the mind → one wave ripples out →
 *   branches of light unfurl outward from the core, then split into finer twigs.
 * Ambient (afterwards, paced like slow breathing):
 *   the core breathes on a 6s inhale/exhale; a soft ripple leaves it on each breath;
 *   now and then a thought travels from the core out along a branch; the tips glimmer.
 * With reduced motion it renders the settled state and nothing moves.
 */

const CORE = { x: 366, y: 246 };
const BREATH_S = 6;

type Pt = [number, number];

/**
 * The mind's branches, unfurling from the core: seven primary branches, each splitting
 * into two fine twigs, all inside the skull (x 270–472, y 162–318).
 */
const PRIMARY: { tip: Pt; twigs: [Pt, Pt] }[] = [
  { tip: [322, 205], twigs: [[292, 190], [315, 172]] },
  { tip: [360, 190], twigs: [[343, 166], [380, 163]] },
  { tip: [410, 202], twigs: [[440, 191], [424, 172]] },
  { tip: [445, 250], twigs: [[470, 232], [472, 270]] },
  { tip: [405, 292], twigs: [[430, 306], [398, 318]] },
  { tip: [322, 286], twigs: [[300, 301], [336, 316]] },
  { tip: [292, 252], twigs: [[270, 240], [274, 276]] },
];

/** A gently bowed segment: the control point sits off the midpoint, alternating sides. */
const bow = ([x1, y1]: Pt, [x2, y2]: Pt, bend: number) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  const cx = mx + (-(y2 - y1) / len) * len * bend;
  const cy = my + ((x2 - x1) / len) * len * bend;
  return { q: `Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${x2} ${y2}`, d: `M ${x1} ${y1} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${x2} ${y2}` };
};

const CORE_PT: Pt = [CORE.x, CORE.y];
const BRANCHES = PRIMARY.flatMap((b, i) => {
  const trunk = bow(CORE_PT, b.tip, i % 2 ? 0.14 : -0.14);
  return [
    { d: trunk.d, level: 0, order: i },
    ...b.twigs.map((t, j) => ({ d: bow(b.tip, t, j ? 0.18 : -0.18).d, level: 1, order: i * 2 + j })),
  ];
});
const TIPS: { pt: Pt; level: number; order: number }[] = PRIMARY.flatMap((b, i) => [
  { pt: b.tip, level: 0, order: i },
  ...b.twigs.map((t, j) => ({ pt: t, level: 1, order: i * 2 + j })),
]);

/** Pathways a thought travels: from the core out along a branch to one of its twigs. */
const PATHWAYS = [
  [0, 1],
  [3, 0],
  [5, 1],
  [2, 0],
  [6, 1],
].map(([bi, ti]) => {
  const b = PRIMARY[bi];
  const trunk = bow(CORE_PT, b.tip, bi % 2 ? 0.14 : -0.14);
  const twig = bow(b.tip, b.twigs[ti], ti ? 0.18 : -0.18);
  return `M ${CORE.x} ${CORE.y} ${trunk.q} ${twig.q}`;
});
const SIGNAL_TRAVEL_MS = 1900;
const SIGNAL_EVERY_MS = 4200;

/** When each awakening beat starts (seconds after the visual enters view). */
export const AWAKEN = { core: 0.25, wave: 0.55, lines: 0.9, halo: 1.6, settled: 3.1 };

const fillBox = { transformBox: "fill-box", transformOrigin: "center" } as const;

export function MindUnfold({ awake, animate, reduce }: { awake: boolean; animate: boolean; reduce: boolean }) {
  const shown = awake || reduce;
  // The ambient loops take over once the awakening has played out.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!awake || reduce) return;
    const id = window.setTimeout(() => setSettled(true), AWAKEN.settled * 1000);
    return () => window.clearTimeout(id);
  }, [awake, reduce]);
  const live = animate && settled; // on screen, motion allowed, awakening done

  // Thought-signal travelling a pathway (driven by the frame clock so it pauses off screen).
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const clock = useRef(0);
  const sx = useMotionValue(CORE.x);
  const sy = useMotionValue(CORE.y);
  const sOpacity = useMotionValue(0);
  useAnimationFrame((_, delta) => {
    if (!live) return;
    clock.current += Math.min(delta, 64);
    const t = clock.current;
    const n = Math.floor(t / SIGNAL_EVERY_MS);
    const u = t % SIGNAL_EVERY_MS;
    const path = pathRefs.current[n % PATHWAYS.length];
    if (!path || u > SIGNAL_TRAVEL_MS) {
      sOpacity.set(0);
      return;
    }
    const p = u / SIGNAL_TRAVEL_MS;
    const pt = path.getPointAtLength(((1 - Math.cos(Math.PI * p)) / 2) * path.getTotalLength());
    sx.set(pt.x);
    sy.set(pt.y);
    sOpacity.set(Math.min(1, p / 0.15, (1 - p) / 0.15));
  });

  return (
    <g aria-hidden style={{ mixBlendMode: "screen" }}>
      <defs>
        <radialGradient id="lp-mind-core">
          <stop offset="0%" stopColor="rgb(255 240 226)" stopOpacity="0.95" />
          <stop offset="28%" stopColor="rgb(214 196 255)" stopOpacity="0.55" />
          <stop offset="62%" stopColor="rgb(150 124 240)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="rgb(120 90 230)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Core of light: ignites, then breathes. */}
      <motion.circle
        cx={CORE.x}
        cy={CORE.y}
        r={150}
        fill="url(#lp-mind-core)"
        style={fillBox}
        initial={reduce ? false : { opacity: 0, scale: 0.2 }}
        animate={
          live
            ? { opacity: [0.7, 0.95, 0.7, 0.5, 0.7], scale: [1, 1.08, 1, 0.93, 1] }
            : shown
              ? { opacity: 0.7, scale: 1 }
              : undefined
        }
        transition={
          live
            ? { duration: BREATH_S * 2, repeat: Infinity, ease: "easeInOut" }
            : { duration: 1.4, ease: [0.16, 0.8, 0.2, 1], delay: reduce ? 0 : AWAKEN.core }
        }
      />

      {/* The awakening wave: one ring of light across the whole head. */}
      {!reduce && (
        <motion.circle
          cx={CORE.x}
          cy={CORE.y}
          r={60}
          className="lp-mind-ring"
          style={fillBox}
          initial={{ opacity: 0, scale: 0.3 }}
          animate={awake ? { opacity: [0, 0.5, 0], scale: [0.3, 4.2] } : undefined}
          transition={{ duration: 2.2, ease: [0.2, 0.7, 0.2, 1], delay: AWAKEN.wave, times: [0, 0.15, 1] }}
        />
      )}

      {/* Breath ripples: one soft ring leaves the core on each exhale. */}
      {live &&
        [0, 1, 2].map((i) => (
          <motion.circle
            key={i}
            cx={CORE.x}
            cy={CORE.y}
            r={48}
            className="lp-mind-ring"
            style={fillBox}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: [0, 0.4, 0], scale: [0.4, 3.2] }}
            transition={{
              duration: BREATH_S,
              repeat: Infinity,
              ease: "easeOut",
              delay: i * (BREATH_S / 3),
              times: [0, 0.2, 1],
            }}
          />
        ))}

      {/* Branches unfurl outward from the core: trunks first, then the twigs. */}
      {BRANCHES.map((br, i) => (
        <motion.path
          key={`b${i}`}
          d={br.d}
          className={br.level ? "lp-mind-twig" : "lp-mind-edge"}
          initial={reduce ? false : { pathLength: 0, opacity: 0 }}
          animate={shown ? { pathLength: 1, opacity: 1 } : undefined}
          transition={{
            duration: br.level ? 0.6 : 0.75,
            ease: [0.3, 0.6, 0.3, 1],
            delay: reduce ? 0 : AWAKEN.lines + (br.level ? 0.55 + br.order * 0.035 : br.order * 0.07),
          }}
        />
      ))}
      {/* Tips glimmer: they appear as their branch arrives, then breathe unevenly. */}
      <motion.circle
        cx={CORE.x}
        cy={CORE.y}
        r={3.8}
        className="lp-mind-node"
        style={fillBox}
        initial={reduce ? false : { opacity: 0, scale: 0 }}
        animate={shown ? { opacity: 1, scale: 1 } : undefined}
        transition={{ duration: 0.6, delay: reduce ? 0 : AWAKEN.core + 0.2 }}
      />
      {TIPS.map((tp, i) => (
        <motion.circle
          key={`t${i}`}
          cx={tp.pt[0]}
          cy={tp.pt[1]}
          r={tp.level ? 1.7 : 2.4}
          className="lp-mind-node"
          style={fillBox}
          initial={reduce ? false : { opacity: 0, scale: 0 }}
          animate={
            live
              ? { opacity: [0.85, 1, 0.35, 0.85], scale: 1 }
              : shown
                ? { opacity: 0.85, scale: 1 }
                : undefined
          }
          transition={
            live
              ? { duration: 3.4 + (i % 5) * 0.6, repeat: Infinity, ease: "easeInOut", delay: (i % 7) * 0.45 }
              : {
                  duration: 0.45,
                  ease: "easeOut",
                  delay: reduce ? 0 : AWAKEN.lines + (tp.level ? 1.05 + tp.order * 0.035 : 0.6 + tp.order * 0.07),
                }
          }
        />
      ))}

      {/* Signal pathways (invisible guides) and the travelling signal. */}
      {PATHWAYS.map((d, i) => (
        <path
          key={`p${i}`}
          ref={(el) => {
            pathRefs.current[i] = el;
          }}
          d={d}
          fill="none"
          stroke="none"
        />
      ))}
      {!reduce && (
        <>
          <motion.circle r={9} cx={0} cy={0} className="lp-mind-signal-glow" style={{ x: sx, y: sy, opacity: sOpacity }} />
          <motion.circle r={3} cx={0} cy={0} className="lp-mind-signal" style={{ x: sx, y: sy, opacity: sOpacity }} />
        </>
      )}
    </g>
  );
}
